import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, Film, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();
const POLL_MS = 5000;

const ROOM_STATES = [
  { value: "empty-room", label: "Empty room (add virtual furniture)" },
  { value: "already-staged", label: "Already staged (just add motion)" },
];

/**
 * RendyImageOptionsDialog — opened from the film-icon toggle on a gallery
 * photo. Lets the agent pick which Rendy preset / staging style / staging
 * animation / camera movement to apply to THIS image (or none, i.e. the
 * property-wide defaults from Integrations -> Rendy Fields), and a separate
 * "Generate Preview" button that renders a one-image Rendy clip with the
 * currently-selected combo so they can see the result before committing to
 * a full property-wide generation. Polls while the dialog stays open.
 */
export default function RendyImageOptionsDialog({ open, onClose, initial, propertyId, onSave, onRemove, followingCount = 0 }) {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [presetKey, setPresetKey] = useState("");
  const [roomState, setRoomState] = useState("already-staged");
  const [stagingStyleId, setStagingStyleId] = useState("");
  const [stagingAnimationId, setStagingAnimationId] = useState("");
  const [cameraActionKey, setCameraActionKey] = useState("");
  const [applyToFollowing, setApplyToFollowing] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    setApplyToFollowing(false);
    setPresetKey(initial?.rendy_preset_key || "");
    setRoomState(initial?.rendy_staging_input_state || "already-staged");
    setStagingStyleId(initial?.rendy_staging_style_id || "");
    setStagingAnimationId(initial?.rendy_staging_animation_id || "");
    setCameraActionKey(initial?.rendy_camera_action_key || "");
    setPreviewDoc(null);
    setLoading(true);
    axios
      .get(`${API}/rendy/options`, { withCredentials: true })
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions({ presets: [], staging_styles: [], staging_animations: [], camera_movements: [] }))
      .finally(() => setLoading(false));
    if (propertyId && initial?.media_url) {
      axios
        .get(`${API}/rendy/properties/${propertyId}/gallery/preview`, {
          params: { media_url: initial.media_url },
          withCredentials: true,
        })
        .then(({ data }) => setPreviewDoc(data.preview || null))
        .catch(() => {});
    }
    return stopPolling;
  }, [open, initial, propertyId]);

  // Poll for progress while a preview is in flight and the dialog is open.
  useEffect(() => {
    stopPolling();
    if (!open || !previewDoc || !propertyId) return;
    if (!["generating", "exporting"].includes(previewDoc.status)) return;
    pollRef.current = setInterval(() => {
      axios
        .post(`${API}/rendy/properties/${propertyId}/gallery/preview/${previewDoc.video_id}/refresh`, {}, { withCredentials: true })
        .then(({ data }) => setPreviewDoc(data))
        .catch(() => {});
    }, POLL_MS);
    return stopPolling;
  }, [open, previewDoc?.video_id, previewDoc?.status, propertyId]);

  const selectedPreset = options?.presets?.find((p) => p.key === presetKey);
  const isStaging = !!selectedPreset?.staging;

  const buildPatch = () => ({
    use_for_rendy: true,
    rendy_preset_key: presetKey || null,
    rendy_staging_input_state: isStaging ? roomState : null,
    rendy_staging_style_id: isStaging && roomState === "empty-room" ? stagingStyleId || null : null,
    rendy_staging_animation_id: isStaging ? stagingAnimationId || null : null,
    rendy_camera_action_key: !isStaging ? cameraActionKey || null : null,
  });

  const handleGeneratePreview = () => {
    if (!propertyId || !initial?.media_url) return;
    const patch = buildPatch();
    setPreviewStarting(true);
    axios
      .post(
        `${API}/rendy/properties/${propertyId}/gallery/preview`,
        {
          media_url: initial.media_url,
          rendy_preset_key: patch.rendy_preset_key,
          rendy_staging_input_state: patch.rendy_staging_input_state,
          rendy_staging_style_id: patch.rendy_staging_style_id,
          rendy_staging_animation_id: patch.rendy_staging_animation_id,
          rendy_camera_action_key: patch.rendy_camera_action_key,
        },
        { withCredentials: true }
      )
      .then(({ data }) => setPreviewDoc(data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Couldn't start the Rendy preview.");
      })
      .finally(() => setPreviewStarting(false));
  };

  const handleSave = () => {
    onSave(buildPatch(), { applyToFollowing });
    onClose();
  };

  const previewBusy = previewStarting || ["generating", "exporting"].includes(previewDoc?.status);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="rendy-image-options-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Include in Rendy video
          </DialogTitle>
          <DialogDescription>
            Choose how this photo should be animated. Leave anything as "None" to use the property's default settings.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading Rendy options…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-dashed border-border bg-muted/40 p-3 space-y-2" data-testid="rendy-image-preview-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium flex items-center gap-1.5">
                  <Film className="h-3.5 w-3.5" /> Preview with current settings
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGeneratePreview}
                  disabled={previewBusy || !propertyId || !initial?.media_url}
                  data-testid="rendy-generate-preview-btn"
                >
                  {previewBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  {previewDoc ? "Regenerate Preview" : "Generate Preview"}
                </Button>
              </div>
              {!previewDoc ? (
                <p className="text-xs text-muted-foreground">No preview yet — pick your options below, then generate one.</p>
              ) : previewDoc.status === "ready" && previewDoc.videos?.[0]?.url ? (
                <video
                  src={previewDoc.videos[0].url}
                  poster={previewDoc.videos[0].poster}
                  controls
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video bg-black object-cover"
                  data-testid="rendy-image-preview-video"
                />
              ) : previewDoc.status === "failed" ? (
                <p className="text-xs text-destructive" data-testid="rendy-image-preview-failed">
                  {previewDoc.error || "Preview generation failed."}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="rendy-image-preview-pending">
                  <Loader2 className="h-3 w-3 animate-spin" /> Rendering this preset/camera on this photo…
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Preset</label>
              <select
                value={presetKey}
                onChange={(e) => setPresetKey(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="rendy-image-preset-select"
              >
                <option value="">None (use default)</option>
                {(options?.presets || []).filter((p) => p.enabled !== false).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}{p.staging ? " (staging)" : ""}
                  </option>
                ))}
              </select>
              {selectedPreset?.description && (
                <p className="text-xs text-muted-foreground">{selectedPreset.description}</p>
              )}
            </div>

            {isStaging ? (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-medium">Room state</label>
                  <select
                    value={roomState}
                    onChange={(e) => setRoomState(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="rendy-image-room-state-select"
                  >
                    {ROOM_STATES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                {roomState === "empty-room" && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium">Staging style</label>
                    <select
                      value={stagingStyleId}
                      onChange={(e) => setStagingStyleId(e.target.value)}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="rendy-image-staging-style-select"
                    >
                      <option value="">None</option>
                      {(options?.staging_styles || []).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-xs font-medium">Staging animation</label>
                  <select
                    value={stagingAnimationId}
                    onChange={(e) => setStagingAnimationId(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="rendy-image-staging-animation-select"
                  >
                    <option value="">None</option>
                    {(options?.staging_animations || []).map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div className="space-y-1">
                <label className="text-xs font-medium">Camera movement</label>
                <select
                  value={cameraActionKey}
                  onChange={(e) => setCameraActionKey(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="rendy-image-camera-movement-select"
                >
                  <option value="">None (auto)</option>
                  {(options?.camera_movements || []).map((m) => (
                    <option key={m.key} value={m.key}>{m.key}</option>
                  ))}
                </select>
              </div>
            )}

            {followingCount > 0 && (
              <label
                className="flex items-start gap-2 border border-dashed border-border bg-muted/40 p-3 text-sm cursor-pointer"
                data-testid="rendy-apply-to-following-label"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-primary"
                  checked={applyToFollowing}
                  onChange={(e) => setApplyToFollowing(e.target.checked)}
                  data-testid="rendy-apply-to-following-checkbox"
                />
                <span>
                  <span className="font-medium">Apply these settings to all following images</span>
                  <span className="block text-xs text-muted-foreground">
                    Marks the {followingCount} photo{followingCount === 1 ? "" : "s"} after this one for Rendy with the same preset
                    {isStaging ? ", room state and staging animation" : " and camera movement"}.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {initial?.use_for_rendy ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => { onRemove(); onClose(); }}
              data-testid="rendy-image-remove-btn"
            >
              Remove from Rendy
            </Button>
          ) : <span />}
          <Button onClick={handleSave} data-testid="rendy-image-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
